import { Action, createReducer, on } from '@ngrx/store';
import { SearchRequest } from 'src/app/models/requests/search-request.model';
import {
  N0AvailabilityResponse,
  N0LowFareAvailabilityByDestinationResponse,
  N0LowFareAvailabilityByOriginResponse,
  N0LowFareAvailabilitySummaryOriginInfo,
} from 'src/app/models/responses/availability-response.model';
import { N0LowFareTrip } from 'src/app/models/responses/lowfare-availability-response.model';
import {
  AvailabilityActionTypes,
  availabilityChangePassengers,
  availabilityConvertToOneWay,
  availabilityClearPromoCode,
  availabilityDisplayView,
  availabilityGetRouteScheduleSuccess,
  availabilityRedirectToDohop,
  availabilitySearchAll,
  availabilitySearchAllHome,
  availabilitySearchAllModify,
  availabilitySearchByQueryParams,
  availabilitySearchFromDestinations,
  availabilitySearchLowFaresMonthlyByOriginSuccess,
  availabilitySearchFareAvailabilitySuccess,
  availabilitySearchFares,
  availabilitySearchLowFareAvailabilitySuccess,
  availabilitySellTripSelectionByQueryParams,
  availabilitySellTripSelectionSuccess,
  availabilityStoreAvailabilitySelection,
  availabilityStoreAvailabilitySelections,
  clearAvailabilitySelection,
  searchRequestUpdate,
  setIsSearchByQueryState,
  setPreferredCabin,
  selectCalendarDate,
  updateCalendarSearchCriteria,
  updateCalendarWeekNumber,
  selectCalendarDay,
  setSearchTripType,
  availabilityBackToCalendar,
  availabilityInvalidSearchByQueryParams,
  availabilitySearchLowFaresSummarySuccess,
  availabilityGetRouteSchedule,
  availabilityConvertToOneWaySuccess,
  availabilitySearchFromDestinationsWithSchedule,
  availabilitySearchLowFaresMonthlyByDestinationSuccess,
} from '@actions/availability.action';
import { updatePrimaryContactSuccess } from '@actions/passenger.action';
import { TripType } from '@constants/home.constants';
import { AvailabilitySelection } from '@models/availability.model';
import { DateTime } from 'luxon';
import {
  DATETIME_FORMAT_TO_SECONDS,
  DATE_FORMAT_YEAR_MONTH_DATE,
} from '@constants/date-format.constants';
import {
  BookingActionTypes,
  bookingCancelModification,
  bookingRetrieveBooking,
  bookingUpgradeCabin,
  upgradeToEconomyClassicActionSuccess,
} from '@actions/booking.action';
import { RouteSchedule } from '@models/requests/route-schedule-request.model';
import {
  coverGeniusClearQuote,
  coverGeniusRequestQuoteSuccess,
} from '@actions/cover-genius.action';
import { CoverGeniusQuoteResponse } from '@models/responses/cover-genius-quote-response.model';
import {
  DisplayViewOptions,
  DisplayViewOptionsEnum,
} from '@constants/flights.constants';
import { CabinClassCode } from '@constants/cabin.constants';
import { updateSearchCriteria } from '@utils/availability.utils';
import { routeDiscountMap } from '@constants/app.constants';
import { updateSelectedCurrencyCode } from '@actions/culture.action';
import { extrasSellExtrasBundleSuccess } from '@actions/extras.action';
import {
  initUpgradeFareToPremium,
} from '@actions/seat.action';
import { MetaId } from "@models/meta-id.model";

export interface State {
  activeWeek: number | null;
  autoSelect: boolean;
  availability: N0AvailabilityResponse | null;
  coverGenius: CoverGeniusQuoteResponse | null;
  lowFareByOrigin: N0LowFareAvailabilityByOriginResponse | null;
  lowFareByDestination: N0LowFareAvailabilityByDestinationResponse | null;
  displayView: DisplayViewOptions;
  hasInvalidPromoCode: boolean;
  isSearchByQuery: boolean;
  lowFareAvailability: N0LowFareTrip[] | null;
  lowFareSummaries: N0LowFareAvailabilitySummaryOriginInfo[] | null;
  metaId: MetaId | null | undefined;
  preferredCabinClass: CabinClassCode;
  redirectId?: string;
  routeSchedule: RouteSchedule | null;
  searchAvailabilityInput: SearchRequest | null;
  searchInput: SearchRequest | null;
  selectedCabinClass: CabinClassCode | null;
  selectedDates: DateTime[];
  selection: AvailabilitySelection | null;
  showLowFareSelectedOutboundDate: boolean;
  showLowFareSelectedReturnDate: boolean;
  sourceOrganization?: string;
  stationsFromDestinationMap: string[] | null;
  tripType: TripType;
  recentScheduleNotFound: boolean;
}

export const INITIAL_STATE: State = {
  activeWeek: null,
  availability: null,
  coverGenius: null,
  lowFareAvailability: null,
  lowFareByOrigin: null,
  lowFareByDestination: null,
  metaId: null,
  searchAvailabilityInput: null,
  searchInput: null,
  selection: null,
  sourceOrganization: undefined,
  tripType: TripType.roundTrip,
  redirectId: undefined,
  routeSchedule: null,
  displayView: DisplayViewOptionsEnum.LOW_FARE,
  showLowFareSelectedOutboundDate: false,
  showLowFareSelectedReturnDate: false,
  preferredCabinClass: CabinClassCode.ECONOMY,
  selectedCabinClass: null,
  selectedDates: [],
  stationsFromDestinationMap: null,
  autoSelect: false,
  isSearchByQuery: false,
  hasInvalidPromoCode: false,
  lowFareSummaries: null,
  recentScheduleNotFound: false,
};

const reducer = createReducer(
  INITIAL_STATE,

  on(
    // eslint-disable-next-line ngrx/avoid-duplicate-actions-in-reducer
    availabilitySearchAll,
    availabilitySearchAllHome,
    availabilitySearchAllModify,
    availabilitySearchByQueryParams,
    availabilitySearchFromDestinations,
    availabilitySellTripSelectionByQueryParams,
    availabilityRedirectToDohop,
    bookingUpgradeCabin,
    (state, action): State => {
      const searchByQuery =
        action.type === AvailabilityActionTypes.searchByQueryParams ||
        action.type === AvailabilityActionTypes.searchFromDestinations
          ? action.payload.lowFare
            ? false
            : true
          : state.isSearchByQuery;
      return {
        ...state,
        hasInvalidPromoCode: false,
        searchInput: {
          ...action.payload,
          clearBooking:
            action.type === AvailabilityActionTypes.searchByQueryParams,
        },
        searchAvailabilityInput: {
          ...action.payload,
          criteria: action.payload.criteria?.map((c) => ({
            ...c,
            beginDate: '',
          })),
          clearBooking:
            action.type === AvailabilityActionTypes.searchByQueryParams,
        },
        tripType: action.tripType, //TODO: Remove this after verify.

        // include source org from state if not overidden from deep link search, will keep sending for this session
        sourceOrganization:
          action.type === AvailabilityActionTypes.searchByQueryParams
            ? action.sourceOrganization
            : state.sourceOrganization,
        redirectId:
          action.type === AvailabilityActionTypes.searchByQueryParams
            ? action.redirectId
            : state.redirectId,
        autoSelect:
          action.type === AvailabilityActionTypes.searchByQueryParams ||
          action.type === AvailabilityActionTypes.searchFromDestinations
            ? action.payload.lowFare
              ? false
              : true
            : state.autoSelect,
        selection:
          action.type === AvailabilityActionTypes.searchByQueryParams ||
          action.type === AvailabilityActionTypes.searchFromDestinations
            ? null
            : state.selection,
        isSearchByQuery: searchByQuery,
        displayView:
          action.type === BookingActionTypes.upgradeCabin ||
          (searchByQuery &&
            action.type !== AvailabilityActionTypes.searchAllModify)
            ? DisplayViewOptionsEnum.CONFIRM_TRIP
            : DisplayViewOptionsEnum.LOW_FARE,
        selectedDates:
          action.type === AvailabilityActionTypes.searchByQueryParams
            ? action.payload.criteria.map((c) =>
                DateTime.fromFormat(c.beginDate, DATE_FORMAT_YEAR_MONTH_DATE),
              )
            : state.selectedDates,
        metaId:
          action.type === AvailabilityActionTypes.searchByQueryParams ||
          action.type === AvailabilityActionTypes.sellTripSelectionByQueryParams
            ? action.metaId
            : state.metaId,
      };
    },
  ),

  on(availabilityConvertToOneWay, (state): State => {
    return {
      ...state,
      hasInvalidPromoCode: false,
      searchInput: state.searchInput && {
        ...state.searchInput,
        criteria: state.searchInput.criteria.slice(0, 1),
      },
      searchAvailabilityInput: state.searchAvailabilityInput && {
        ...state.searchAvailabilityInput,
        criteria: state.searchAvailabilityInput.criteria.slice(0, 1),
      },
      tripType: TripType.oneWay,
      displayView: DisplayViewOptionsEnum.CONFIRM_TRIP,
      availability: {
        ...state.availability,
        results: state.availability?.results?.slice(0, 1)
      }
    };
  }),

  // clear store values when search starts
  on(
    // eslint-disable-next-line ngrx/avoid-duplicate-actions-in-reducer
    availabilitySearchAll,
    availabilitySearchAllHome,
    availabilitySearchAllModify,
    availabilitySearchFromDestinations,
    bookingUpgradeCabin,
    (state, _): State => {
      return {
        ...state,
        availability: null,
        lowFareAvailability: null,
        selection: null,
        coverGenius: null,
        showLowFareSelectedOutboundDate: false,
        showLowFareSelectedReturnDate: false,
        selectedDates: [],
        activeWeek: null,
        selectedCabinClass: null,
      };
    },
  ),

  on(
    availabilityInvalidSearchByQueryParams,
    (state): State => ({
      ...state,
      autoSelect: false,
      isSearchByQuery: false,
      searchInput: null,
      hasInvalidPromoCode: false,
    }),
  ),

  on(
    availabilitySearchFromDestinationsWithSchedule,
    (state, action): State => ({
      ...state,
      stationsFromDestinationMap: action?.payload?.criteria?.[0]
        ? [
            action?.payload?.criteria?.[0].origin,
            action?.payload?.criteria?.[0].destination,
          ]
        : null,
    }),
  ),

  on(
    availabilitySearchLowFaresMonthlyByOriginSuccess,
    (state, action): State => {
      return {
        ...state,
        lowFareByOrigin: action.payload,
      };
    },
  ),

  on(
    availabilitySearchLowFaresMonthlyByDestinationSuccess,
    (state, action): State => {
      return {
        ...state,
        lowFareByDestination: action.payload,
      };
    },
  ),

  on(availabilitySearchLowFaresSummarySuccess, (state, action): State => {
    return {
      ...state,
      lowFareSummaries: action.payload.summaries,
    };
  }),

  on(setIsSearchByQueryState, (state, action): State => {
    return {
      ...state,
      isSearchByQuery: action.state,
    };
  }),

  on(
    coverGeniusClearQuote,
    bookingCancelModification,
    bookingRetrieveBooking,
    (state): State => {
    return {
      ...state,
      coverGenius: null,
    };
  }),

  // clear store value for index or all availability if no index provided
  on(availabilitySearchFares, (state, action): State => {
    if (action.tripIndex === undefined) {
      return {
        ...state,
        availability: null,
      };
    }

    const trips = state.selection?.sellKeys
      ? state.selection.sellKeys.slice()
      : [];
    const journeys = state.selection?.journeys
      ? state.selection.journeys.slice()
      : [];

    //Clears selected trip and journey for current trip index
    if (action.clearSelectedFares) {
      action.clearSelectedFares.map((i) => {
        if (trips[i]) {
          trips[i] = null;
        }
        if (journeys[i]) {
          journeys[i] = null;
        }
      });
    }

    return {
      ...state,
      searchInput: state.searchInput && {
        ...state.searchInput,
        criteria: state.searchInput.criteria.map((c, index) => {
          return index === action.tripIndex && action.payload.criteria[0]
            ? action.payload.criteria[0]
            : c;
        }),
      },
      searchAvailabilityInput: state.searchInput && {
        ...state.searchInput,
        criteria: state.searchInput.criteria.map((c, index) => {
          return index === action.tripIndex && action.payload.criteria[0]
            ? action.payload.criteria[0]
            : c;
        }),
      },
      availability: {
        ...state.availability,
        results: state.availability?.results?.map((r, index) =>
          index === action.tripIndex ? { trips: [] } : r,
        ),
      },
      selection: { sellKeys: trips, journeys: journeys },
      showLowFareSelectedOutboundDate:
        action.showSelectedOutboundDate ??
        state.showLowFareSelectedOutboundDate,
      showLowFareSelectedReturnDate:
        action.showSelectedReturnDate ?? state.showLowFareSelectedReturnDate,
      displayView: DisplayViewOptionsEnum.LOW_FARE,
      isSearchByQuery: false,
    };
  }),

  on(
    availabilitySearchFareAvailabilitySuccess,
    availabilityConvertToOneWaySuccess,
    (state, action): State => {
    if (action.tripIndex === undefined) {
      if (
        state.searchAvailabilityInput?.criteria &&
        state.searchAvailabilityInput.criteria.length > 1 &&
        action.showStrikethroughPrice
      ) {
        const origin = state.searchAvailabilityInput?.criteria[0]?.origin;
        const destination =
          state.searchAvailabilityInput?.criteria[0]?.destination;
        const route = `${origin}-${destination}`;
        const discount = routeDiscountMap[route] ?? 0.2;
        return {
          ...state,
          availability: {
            ...action.payload,
            results: action?.payload?.results?.map((tripResult) => ({
              ...tripResult,
              trips: tripResult.trips.map((tripDateMarket) => ({
                ...tripDateMarket,
                journeysAvailableByMarket:
                  tripDateMarket.journeysAvailableByMarket?.map((journey) => ({
                    ...journey,
                    fares: journey.fares.map((fare) => ({
                      ...fare,
                      details: {
                        ...fare.details,
                        totals: {
                          ...fare.details.totals,
                          roundedStrikethroughTotal: Math.ceil(
                            fare.details.totals.publishedTotal /
                              (1 - discount) +
                              fare.details.totals.taxesAndFeesTotal,
                          ),
                        },
                      },
                    })),
                  })),
              })),
            })),
          },
          displayView: action.selectionOnTripConfirmationView
            ? DisplayViewOptionsEnum.CONFIRM_TRIP
            : state.displayView,
          isSearchByQuery:
            action.selectionOnTripConfirmationView ?? state.isSearchByQuery,
        };
      }

      return {
        ...state,
        availability: action.payload,
        displayView: action.selectionOnTripConfirmationView
          ? DisplayViewOptionsEnum.CONFIRM_TRIP
          : state.displayView,
        isSearchByQuery:
          action.selectionOnTripConfirmationView ?? state.isSearchByQuery,
      };
    }

    return {
      ...state,
      availability: {
        ...state.availability,
        results: state.availability?.results?.map((r, index) =>
          index === action.tripIndex &&
          action.payload?.results &&
          action.payload.results[0]
            ? action.payload.results[0]
            : r,
        ),
      },
      displayView: action.selectionOnTripConfirmationView
        ? DisplayViewOptionsEnum.CONFIRM_TRIP
        : DisplayViewOptionsEnum.FARE_PICKER,
      isSearchByQuery:
        action.selectionOnTripConfirmationView ?? state.isSearchByQuery,
    };
  }),

  on(availabilitySearchLowFareAvailabilitySuccess, (state, action): State => {
    if (action.tripIndex === undefined) {
      return {
        ...state,
        lowFareAvailability: action.payload,
      };
    }

    return {
      ...state,
      lowFareAvailability: state.lowFareAvailability
        ? state.lowFareAvailability.map((r, index) =>
            index === action.tripIndex && action.payload && action.payload[0]
              ? {
                  ...r,
                  cityPair: action.payload[0].cityPair,
                  cabins: r.cabins.map((c) => ({
                    ...c,
                    lowFareAmounts: c.lowFareAmounts
                      .filter(
                        (a) =>
                          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                          !action.payload[0]!.cabins.find(
                            (cabin) => c.cabinName === cabin.cabinName,
                          )?.lowFareAmounts.some(
                            (lfa) => lfa.departureDate === a.departureDate,
                          ),
                      )
                      .concat(
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        action.payload[0]!.cabins.find(
                          (cabin) => c.cabinName === cabin.cabinName,
                        )?.lowFareAmounts ?? [],
                      ),
                  })),
                }
              : r,
          )
        : null,
    };
  }),

  on(
    availabilitySellTripSelectionSuccess,
    (state): State => ({
      ...state,
      displayView: DisplayViewOptionsEnum.CONFIRM_TRIP,
    }),
  ),

  on(availabilitySellTripSelectionByQueryParams, (state, action): State => {
    return {
      ...state,
      selection: {
        sellKeys: action.sellKeys,
        journeys: [],
      },
    };
  }),

  on(availabilityConvertToOneWaySuccess,
    (state, action): State => {
    return {
      ...state,
      selection: {
        sellKeys: state.selection?.sellKeys.slice(0,1) ?? [],
        journeys: [action.payload?.results?.[0]?.trips?.[0]?.journeysAvailableByMarket?.find(j => j.journeyKey === state.selection?.journeys[0]?.journeyKey) ?? null]
      },
    };
  }),

  on(upgradeToEconomyClassicActionSuccess, initUpgradeFareToPremium, (state, action): State => {
    return {
      ...state,
      selection: {
        sellKeys: action.sellKeys,
        journeys: state.selection?.journeys ?? []
      },
    };
  }),

  on(availabilityStoreAvailabilitySelection, (state, action): State => {
    const trips = state.selection?.sellKeys
      ? state.selection.sellKeys.slice()
      : [];
    trips[action.index] = action.payload;
    const journeys = state.selection?.journeys
      ? state.selection.journeys.slice()
      : [];
    journeys[action.index] = action.journey;

    // ensure arrays have values set for every index up to the index of this action,
    // otherwise validation logic that relies on them being arrays with sequential indices
    // will fail.
    for (let journeyIndex = 0; journeyIndex < action.index; ++journeyIndex) {
      if (!(journeyIndex in journeys)) {
        journeys[journeyIndex] = null;
      }
    }

    for (let tripIndex = 0; tripIndex < action.index; ++tripIndex) {
      if (!(tripIndex in trips)) {
        trips[tripIndex] = null;
      }
    }

    return {
      ...state,
      selection: { sellKeys: trips, journeys: journeys },
      displayView: action.displayView ?? state.displayView,
      activeWeek: null,
    };
  }),

  on(availabilityStoreAvailabilitySelections, (state, action): State => {
    return {
      ...state,
      selection: {
        sellKeys: action.sellKeys,
        journeys: action.journeys,
      },
      autoSelect: false,
    };
  }),

  // Clear redirectId value once it is getting populated to primary contacts address object.
  on(
    updatePrimaryContactSuccess,
    (state): State => ({
      ...state,
      redirectId: undefined,
    }),
  ),

  on(availabilityGetRouteSchedule, (state): State => {
    return {
      ...state,
      recentScheduleNotFound: false,
    };
  }),

  on(availabilityGetRouteScheduleSuccess, (state, action): State => {
    return {
      ...state,
      recentScheduleNotFound: !action.schedule,
      routeSchedule: {
        ...state.routeSchedule,
        [`${action.request.origin}-${action.request.destination}`]:
          action.schedule?.map((routeDate) => ({
            ...routeDate,
            scheduleDay: DateTime.fromFormat(
              routeDate.scheduleDay,
              DATETIME_FORMAT_TO_SECONDS,
            ).toJSDate(),
          })),
      },
    };
  }),

  on(coverGeniusRequestQuoteSuccess, (state, action): State => {
    return {
      ...state,
      coverGenius: action.payload,
    };
  }),

  on(availabilityDisplayView, (state, action): State => {
    return {
      ...state,
      displayView: action.mode,
      activeWeek: null,
    };
  }),

  on(searchRequestUpdate, (state, action): State => {
    return {
      ...state,
      searchInput: action.searchRequest,
    };
  }),

  on(setPreferredCabin, (state, action): State => {
    return {
      ...state,
      preferredCabinClass: action.cabinClass,
      selectedCabinClass: action.cabinClass,
    };
  }),

  on(clearAvailabilitySelection, (state): State => {
    return {
      ...state,
      selection: null,
      selectedCabinClass: null,
    };
  }),

  on(availabilityChangePassengers, (state, action): State => {
    return {
      ...state,
      searchInput: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ...state.searchInput!,
        passengers: action.passengers,
      },
      searchAvailabilityInput: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ...state.searchAvailabilityInput!,
        passengers: action.passengers,
      },
    };
  }),

  on(availabilityClearPromoCode, (state, _): State => {
    return {
      ...state,
      hasInvalidPromoCode: true,
      searchInput: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ...state.searchInput!,
        promotionCode: '',
      },
      searchAvailabilityInput: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ...state.searchAvailabilityInput!,
        promotionCode: '',
      },
    };
  }),
  on(updateCalendarSearchCriteria, (state, action): State => {
    if (!state.searchInput) {
      return state;
    }

    const searchInput = { ...state.searchInput };

    searchInput.criteria = searchInput.criteria.map((c, index) => {
      const criteriaDate = DateTime.fromISO(c.beginDate, { zone: 'local' });
      if (
        index >= action.tripIndex ||
        (index < action.tripIndex && criteriaDate > action.beginDate)
      ) {
        return {
          ...c,
          beginDate: action.beginDate
            .startOf('month')
            .toFormat(DATE_FORMAT_YEAR_MONTH_DATE),
          endDate: action.beginDate
            .endOf('month')
            .toFormat(DATE_FORMAT_YEAR_MONTH_DATE),
        };
      }
      const currentDate = DateTime.fromISO(c.beginDate, { zone: 'local' });
      return {
        ...c,
        beginDate: currentDate
          .startOf('month')
          .toFormat(DATE_FORMAT_YEAR_MONTH_DATE),
        endDate: currentDate
          .endOf('month')
          .toFormat(DATE_FORMAT_YEAR_MONTH_DATE),
      };
    });

    return {
      ...state,
      searchInput: searchInput,
      activeWeek: action.activeWeek ?? state.activeWeek,
    };
  }),

  on(updateCalendarWeekNumber, (state, action): State => {
    return {
      ...state,
      activeWeek: action.weekNumber,
    };
  }),

  on(selectCalendarDate, (state, action): State => {
    if (!state.searchAvailabilityInput) {
      return state;
    }

    if (!state.searchInput) {
      return state;
    }

    const { searchInput, searchAvailabilityInput, selectedDates } =
      updateSearchCriteria(
        { ...state.searchAvailabilityInput },
        { ...state.searchInput },
        action.tripIndex,
        action.beginDate,
        [...state.selectedDates],
      );
    return {
      ...state,
      searchInput,
      searchAvailabilityInput: searchAvailabilityInput,
      selectedDates: selectedDates,
      displayView: DisplayViewOptionsEnum.FARE_PICKER,
      activeWeek: action.weekNumber,
      selection: {
        sellKeys:
          state.selection?.sellKeys.map((sellKey, index) =>
            index === action.tripIndex ? null : sellKey,
          ) ?? [],
        journeys:
          state.selection?.journeys.map((journey, index) =>
            index === action.tripIndex ? null : journey,
          ) ?? [],
      },
    };
  }),

  on(selectCalendarDay, (state, action): State => {
    if (!state.searchAvailabilityInput) {
      return state;
    }

    if (!state.searchInput) {
      return state;
    }

    const { searchInput, searchAvailabilityInput, selectedDates } =
      updateSearchCriteria(
        { ...state.searchAvailabilityInput },
        { ...state.searchInput },
        action.tripIndex,
        action.beginDate,
        [...state.selectedDates],
      );
    return {
      ...state,
      searchInput,
      searchAvailabilityInput: searchAvailabilityInput,
      selectedDates: selectedDates,
    };
  }),

  on(setSearchTripType, (state, action): State => {
    return {
      ...state,
      tripType: action.tripType,
    };
  }),

  on(availabilityBackToCalendar, (state, _): State => {
    return {
      ...state,
      displayView: DisplayViewOptionsEnum.LOW_FARE,
      selectedDates: [],
      activeWeek: null,
      selection: null,
    };
  }),

  on(
    updateSelectedCurrencyCode,
    (state): State => ({
      ...state,
      isSearchByQuery: false,
    }),
  ),

  on(extrasSellExtrasBundleSuccess, (state, action): State => {
    return {
      ...state,
      selection: {
        sellKeys: action.request.keys,
        journeys: state.selection ? state.selection.journeys : [],
      },
    };
  }),
);

export const availabilityReducer = (state: State, action: Action): State =>
  reducer(state, action);
